import { Link } from 'react-router-dom';

interface RallyPin {
  id: string;
  nominee_type: 'teacher' | 'prospect';
  display_name: string;
  school_or_sport: string | null;
  message: string;
  support_code: string;
  pin_color: string;
  flair: string;
  weight: number;
  posted_at: string;
  expires_at: string;
}

const PIN_COLORS: Record<string, string> = {
  red: '#DC2626',
  blue: '#2563EB',
  green: '#059669',
  yellow: '#D97706',
  purple: '#7C3AED',
};

const PIN_SHADOW_COLORS: Record<string, string> = {
  red: '#DC262630',
  blue: '#2563EB30',
  green: '#05966930',
  yellow: '#D9770630',
  purple: '#7C3AED30',
};

/** Deterministic rotation from pin ID — seeded so it doesn't change on re-render */
function getRotation(id: string): number {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = ((hash << 5) - hash + id.charCodeAt(i)) | 0;
  }
  return (hash % 7) - 3; // -3 to +3 degrees
}

/** Hours remaining until expiry */
function hoursLeft(expiresAt: string): number {
  return Math.max(0, (new Date(expiresAt).getTime() - Date.now()) / (1000 * 60 * 60));
}

/** Relative time since posted */
function timeAgo(postedAt: string): string {
  const hours = (Date.now() - new Date(postedAt).getTime()) / (1000 * 60 * 60);
  if (hours < 1) return 'Just now';
  if (hours < 24) return `${Math.floor(hours)}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export type { RallyPin };

export default function RallyPinCard({ pin }: { pin: RallyPin }) {
  const rotation = getRotation(pin.id);
  const remaining = hoursLeft(pin.expires_at);
  const fading = remaining < 12;
  const color = PIN_COLORS[pin.pin_color] || PIN_COLORS.red;
  const shadowColor = PIN_SHADOW_COLORS[pin.pin_color] || PIN_SHADOW_COLORS.red;

  return (
    <div
      className="break-inside-avoid mb-4 transition-all duration-300"
      style={{
        transform: `rotate(${rotation}deg)`,
        opacity: fading ? 0.5 + (remaining / 12) * 0.5 : 1,
      }}
    >
      <div
        className="relative rounded-lg bg-[#FFFEF5] p-4 pb-5 shadow-md"
        style={{
          boxShadow: `2px 3px 8px ${shadowColor}, 0 1px 2px rgba(0,0,0,0.08)`,
          border: '1px solid #E8E4D4',
        }}
      >
        {/* Thumbtack */}
        <div
          className="absolute -top-2 left-1/2 -translate-x-1/2 flex flex-col items-center"
          style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))' }}
        >
          <div
            className="h-4 w-4 rounded-full"
            style={{
              background: `radial-gradient(circle at 35% 35%, ${color}CC, ${color})`,
              boxShadow: `inset -1px -1px 2px rgba(0,0,0,0.3), 0 2px 4px rgba(0,0,0,0.2)`,
            }}
          />
          <div className="h-1.5 w-0.5 bg-gray-400 -mt-0.5" />
        </div>

        {/* Type badge */}
        <div className="flex items-center justify-between mt-1 mb-2">
          <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold ${
            pin.nominee_type === 'teacher'
              ? 'bg-amber-100 text-amber-700'
              : 'bg-sky-100 text-sky-700'
          }`}>
            {pin.nominee_type === 'teacher' ? 'Teacher' : 'Prospect'}
          </span>
          <span className="text-[10px] text-cocoa/40">{timeAgo(pin.posted_at)}</span>
        </div>

        {/* Name + school/sport */}
        <h3 className="text-sm font-bold text-cocoa leading-tight">{pin.display_name}</h3>
        {pin.school_or_sport && (
          <p className="text-[11px] text-cocoa/50 mt-0.5">{pin.school_or_sport}</p>
        )}

        {/* Message — handwriting feel */}
        <p
          className="mt-2 text-sm text-cocoa/80 leading-relaxed"
          style={{ fontFamily: "'Georgia', 'Times New Roman', serif", fontStyle: 'italic' }}
        >
          &ldquo;{pin.message}&rdquo;
        </p>

        {/* Footer: weight + support button */}
        <div className="mt-3 flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <span className="text-red-400 text-sm">&hearts;</span>
            <span className="text-xs font-semibold text-cocoa/70">{pin.weight}</span>
          </div>

          <Link
            to={`/support/${pin.support_code}`}
            className="rounded-full px-3 py-1 text-[11px] font-semibold text-white transition hover:opacity-90"
            style={{ backgroundColor: color }}
          >
            Support
          </Link>
        </div>

        {/* Expiry indicator */}
        {fading && (
          <div className="absolute bottom-1 right-2 text-[9px] text-cocoa/30">
            {Math.ceil(remaining)}h left
          </div>
        )}

        {/* Subtle paper texture lines */}
        <div
          className="pointer-events-none absolute inset-0 rounded-lg opacity-[0.03]"
          style={{
            backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 23px, #8B7355 24px)',
          }}
        />
      </div>
    </div>
  );
}
